import React from 'react';
import {Box, Checkbox, ColumnLayout, FormField, Input, Link} from "@amzn/awsui-components-react-v3";
import Select from "@amzn/awsui-components-react-v3/polaris/select";
import {WorkflowInstanceUtil} from "src/utils/workflow-instance-util";
import {SelectProps} from "@amzn/awsui-components-react-v3/polaris/select/interfaces";
import {BaseChangeDetail} from '@amzn/awsui-components-react-v3/polaris/input/interfaces';

export interface WorkflowMetadataProps {
    programSelectedOption: SelectProps.Option;
    programOptions: SelectProps.Options;
    selectedSubProgram: SelectProps.Option;
    subProgramOptions: SelectProps.Options;
    targetWebsite: SelectProps.Option;
    targetWebsiteOptions: SelectProps.Options;
    domainName: string;
    disableDomain?: boolean;
    disableTargetWebsiteOption?: boolean;
    isScheduled?: boolean;
    isScheduleDisabled?: boolean;
    startWFLoading?: boolean;
    schemaLoading?: boolean;
    onProgramChangeHandler?: (detail: SelectProps.ChangeDetail) => void;
    onSubProgramChangeHandler?: (detail: SelectProps.ChangeDetail) => void;
    onDomainChange?: (detail: BaseChangeDetail) => void;
    onTargetWebsiteChange?: (detail: SelectProps.ChangeDetail) => void;
    onScheduleStatusChange?: (isScheduleOptionChecked: boolean) => void;
}

export const WorkflowMetadata: React.FC<WorkflowMetadataProps> = (props) => {
    const {
        programSelectedOption,
        programOptions,
        selectedSubProgram,
        subProgramOptions,
        targetWebsite,
        targetWebsiteOptions,
        domainName,
        disableDomain,
        disableTargetWebsiteOption,
        isScheduled,
        isScheduleDisabled,
        startWFLoading,
        schemaLoading,
        onProgramChangeHandler,
        onSubProgramChangeHandler,
        onDomainChange,
        onTargetWebsiteChange,
        onScheduleStatusChange
    } = props
    return (
        <>
            <Box padding={{bottom: "l"}}>
                <ColumnLayout columns={2} borders={"vertical"}>
                    <ColumnLayout columns={1}>
                        <FormField
                            label="Classification Tag (Legacy program)"
                        >
                            <Select
                                selectedOption={programSelectedOption}
                                disabled={Boolean(startWFLoading || schemaLoading)}
                                onChange={({detail}) => {
                                    onProgramChangeHandler && onProgramChangeHandler(detail);
                                }}
                                data-testid={"select-program-field"}
                                options={programOptions}
                                selectedAriaLabel="Selected"
                                filteringType="auto"
                            />
                        </FormField>
                        {!isScheduled && selectedSubProgram && <FormField
                            label={<>Select Sub Classification Tag (Legacy Sub program)</>}>
                            <Select
                                selectedOption={selectedSubProgram}
                                disabled={startWFLoading || schemaLoading || subProgramOptions.length === 0}
                                onChange={({detail}) => {
                                    onSubProgramChangeHandler && onSubProgramChangeHandler(detail);
                                }}
                                options={subProgramOptions}
                                selectedAriaLabel="Selected"
                                data-testid={'select-sub-program-field'}
                                filteringType="auto"
                            />
                        </FormField>
                        }
                        {WorkflowInstanceUtil.isDomainFieldVisible(programSelectedOption) && <FormField
                            label={<>Domain name</>}>
                            <Input
                                placeholder={"www.walmart.com"}
                                disabled={Boolean(!WorkflowInstanceUtil.isDomainFieldVisible(programSelectedOption) || disableDomain)}
                                onChange={({detail}) => onDomainChange && onDomainChange(detail)}
                                value={domainName}
                                data-testid={"domain-input-field"}
                            />
                        </FormField>}
                        {WorkflowInstanceUtil.isDomainFieldVisible(programSelectedOption) && <FormField
                            label={<>Select targeted website</>}>
                            <Select
                                selectedOption={targetWebsite}
                                disabled={startWFLoading || schemaLoading || programSelectedOption.value == "" || Boolean(disableTargetWebsiteOption)}
                                onChange={({detail}) => {
                                    onTargetWebsiteChange && onTargetWebsiteChange(detail);
                                }}
                                options={targetWebsiteOptions}
                                data-testid={'select-target-website-field'}
                                selectedAriaLabel="Selected"
                                filteringType="auto"
                            />
                        </FormField>
                        }
                    </ColumnLayout>
                    <ColumnLayout columns={1}>
                        <FormField
                            label={<>&nbsp;</>}>
                            <Checkbox
                                onChange={({detail}) => onScheduleStatusChange && onScheduleStatusChange(detail.checked)}
                                checked={Boolean(isScheduled)}
                                disabled={Boolean(isScheduleDisabled)}
                                data-testid={"schedule-wi-checkbox"}
                            >
                                Schedule Workflow Instance?
                            </Checkbox>
                        </FormField>
                        {WorkflowInstanceUtil.isContinuousProgramVariantSupported(programSelectedOption.label) &&
                            <FormField
                                label={<>&nbsp;</>}>
                                <Checkbox
                                    checked={true}
                                    disabled={true}
                                    data-testid={"continuous-wi-checkbox"}
                                >
                                    Is Continuous Workflow
                                </Checkbox>
                            </FormField>}
                    </ColumnLayout>
                </ColumnLayout>
            </Box>
        </>
    )
}

export interface WorkflowCapabilityMetadataProps {
    capabilitySelectedOption: SelectProps.Option;
    capabilityOptions: SelectProps.Options;
    primitiveSelectedOption: SelectProps.Option;
    primitiveOptions: SelectProps.Options;
    workflowDefinitionOption: SelectProps.Option;
    workflowDefinitionOptions: SelectProps.Options;
    startWFLoading?: boolean;
    schemaLoading?: boolean;
    onCapabilityChangeHandler?: (detail: SelectProps.ChangeDetail) => void;
    onPrimitiveChangeHandler?: (detail: SelectProps.ChangeDetail) => void;
    onWorkflowDefinitionChangeHandler?: (detail: SelectProps.ChangeDetail) => void;
}


export const WorkflowCapabilityMetadata: React.FC<WorkflowCapabilityMetadataProps> = (props) => {
    const {
        capabilitySelectedOption,
        capabilityOptions,
        primitiveSelectedOption,
        primitiveOptions,
        workflowDefinitionOption,
        workflowDefinitionOptions,
        startWFLoading,
        schemaLoading,
        onCapabilityChangeHandler,
        onPrimitiveChangeHandler,
        onWorkflowDefinitionChangeHandler,
    } = props;

    return (
        <>
            <Box padding={{bottom: "l"}}>
                <ColumnLayout columns={2} borders={"vertical"}>
                    <ColumnLayout columns={1}>
                        <FormField label="Capability">
                            <Select
                                selectedOption={capabilitySelectedOption}
                                disabled={Boolean(startWFLoading || schemaLoading)}
                                onChange={({detail}) => {
                                    onCapabilityChangeHandler && onCapabilityChangeHandler(detail);
                                }}
                                data-testid={"select-capability-field"}
                                options={capabilityOptions}
                                selectedAriaLabel="Selected"
                                filteringType="auto"
                            />
                        </FormField>
                        {capabilitySelectedOption?.value && (
                            <FormField label="Primitive">
                                <Select
                                    selectedOption={primitiveSelectedOption}
                                    disabled={Boolean(startWFLoading || schemaLoading)}
                                    onChange={({detail}) => {
                                        onPrimitiveChangeHandler && onPrimitiveChangeHandler(detail);
                                    }}
                                    data-testid={"select-primitive-field"}
                                    options={primitiveOptions}
                                    selectedAriaLabel="Selected"
                                    filteringType="auto"
                                />
                            </FormField>
                        )}
                        {primitiveSelectedOption?.value && (
                            <FormField label="Workflow Definition">
                                <Select
                                    selectedOption={workflowDefinitionOption}
                                    disabled={Boolean(startWFLoading || schemaLoading)}
                                    onChange={({detail}) => {
                                        onWorkflowDefinitionChangeHandler && onWorkflowDefinitionChangeHandler(detail);
                                    }}
                                    data-testid={"select-workflow-definition-field"}
                                    options={workflowDefinitionOptions}
                                    selectedAriaLabel="Selected"
                                    filteringType="auto"
                                />
                            </FormField>
                        )}
                    </ColumnLayout>
                </ColumnLayout>
            </Box>
        </>
    );
};

export interface WorkflowMetadataV1Props extends WorkflowCapabilityMetadataProps{
    classificationTagSelectedOption: SelectProps.Option;
    classificationTagOptions: SelectProps.Options;
    selectedSubClassificationTag: SelectProps.Option;
    subClassificationTagOptions: SelectProps.Options;
    targetWebsite: SelectProps.Option;
    targetWebsiteOptions: SelectProps.Options;
    domainName: string;
    disableDomain?: boolean;
    disableTargetWebsiteOption?: boolean;
    isScheduled?: boolean;
    isScheduleDisabled?: boolean;
    onClassificationTagChangeHandler?: (detail: SelectProps.ChangeDetail) => void;
    onSubClassificationTagChangeHandler?: (detail: SelectProps.ChangeDetail) => void;
    onDomainChange?: (detail: BaseChangeDetail) => void;
    onTargetWebsiteChange?: (detail: SelectProps.ChangeDetail) => void;
    onScheduleStatusChange?: (isScheduleOptionChecked: boolean) => void;
}

export const WorkflowMetadataV1: React.FC<WorkflowMetadataV1Props> = (props) => {
    const {
        capabilitySelectedOption,
        capabilityOptions,
        primitiveSelectedOption,
        primitiveOptions,
        workflowDefinitionOption,
        workflowDefinitionOptions,
        classificationTagSelectedOption,
        classificationTagOptions,
        selectedSubClassificationTag,
        subClassificationTagOptions,
        targetWebsite,
        targetWebsiteOptions,
        domainName,
        disableDomain,
        disableTargetWebsiteOption,
        isScheduled,
        isScheduleDisabled,
        startWFLoading,
        schemaLoading,
        onCapabilityChangeHandler,
        onPrimitiveChangeHandler,
        onWorkflowDefinitionChangeHandler,
        onClassificationTagChangeHandler,
        onSubClassificationTagChangeHandler,
        onDomainChange,
        onTargetWebsiteChange,
        onScheduleStatusChange
    } = props
    return (
        <>
            <WorkflowCapabilityMetadata
                capabilitySelectedOption={capabilitySelectedOption}
                capabilityOptions={capabilityOptions}
                primitiveSelectedOption={primitiveSelectedOption}
                primitiveOptions={primitiveOptions}
                workflowDefinitionOption={workflowDefinitionOption}
                workflowDefinitionOptions={workflowDefinitionOptions}
                startWFLoading={startWFLoading}
                schemaLoading={schemaLoading}
                onCapabilityChangeHandler={onCapabilityChangeHandler}
                onPrimitiveChangeHandler={onPrimitiveChangeHandler}
                onWorkflowDefinitionChangeHandler={onWorkflowDefinitionChangeHandler}
            />
            <Box padding={{bottom: "l"}}>
                <ColumnLayout columns={2} borders={"vertical"}>
                    <ColumnLayout columns={1}>
                        {workflowDefinitionOption?.value && (
                            <FormField label="Classification Tag (Legacy program)">
                                <Select
                                    selectedOption={classificationTagSelectedOption}
                                    disabled={Boolean(startWFLoading || schemaLoading)}
                                    onChange={({detail}) => {
                                        onClassificationTagChangeHandler && onClassificationTagChangeHandler(detail);
                                    }}
                                    data-testid={"select-classification-tag-field"}
                                    options={classificationTagOptions}
                                    selectedAriaLabel="Selected"
                                    filteringType="auto"
                                />
                            </FormField>
                        )}
                        {!isScheduled && classificationTagSelectedOption?.value && selectedSubClassificationTag && (
                            <FormField label="Select Sub Classification Tag (Legacy Sub program)">
                                <Select
                                    selectedOption={selectedSubClassificationTag}
                                    disabled={startWFLoading || schemaLoading || subClassificationTagOptions.length === 0}
                                    onChange={({detail}) => {
                                        onSubClassificationTagChangeHandler && onSubClassificationTagChangeHandler(detail);
                                    }}
                                    options={subClassificationTagOptions}
                                    selectedAriaLabel="Selected"
                                    data-testid={'select-sub-classification-tag-field'}
                                    filteringType="auto"
                                />
                            </FormField>
                        )}
                        {WorkflowInstanceUtil.isDomainFieldVisible(classificationTagSelectedOption) && <FormField
                            label={<>Domain name</>}>
                            <Input
                                placeholder={"www.walmart.com"}
                                disabled={Boolean(!WorkflowInstanceUtil.isDomainFieldVisible(classificationTagSelectedOption) || disableDomain)}
                                onChange={({detail}) => onDomainChange && onDomainChange(detail)}
                                value={domainName}
                                data-testid={"domain-input-field"}
                            />
                        </FormField>}
                        {WorkflowInstanceUtil.isDomainFieldVisible(classificationTagSelectedOption) && <FormField
                            label={<>Select targeted website</>}>
                            <Select
                                selectedOption={targetWebsite}
                                disabled={startWFLoading || schemaLoading || classificationTagSelectedOption.value == "" || Boolean(disableTargetWebsiteOption)}
                                onChange={({detail}) => {
                                    onTargetWebsiteChange && onTargetWebsiteChange(detail);
                                }}
                                options={targetWebsiteOptions}
                                data-testid={'select-target-website-field'}
                                selectedAriaLabel="Selected"
                                filteringType="auto"
                            />
                        </FormField>
                        }
                    </ColumnLayout>
                    <ColumnLayout columns={1}>
                        <FormField
                            label={<>&nbsp;</>}>
                            <Checkbox
                                onChange={({detail}) => onScheduleStatusChange && onScheduleStatusChange(detail.checked)}
                                checked={Boolean(isScheduled)}
                                disabled={Boolean(isScheduleDisabled)}
                                data-testid={"schedule-wi-checkbox"}
                            >
                                Schedule Workflow Instance?
                            </Checkbox>
                        </FormField>
                        {WorkflowInstanceUtil.isContinuousProgramVariantSupported(classificationTagSelectedOption.label) &&
                            <FormField
                                label={<>&nbsp;</>}>
                                <Checkbox
                                    checked={true}
                                    disabled={true}
                                    data-testid={"continuous-wi-checkbox"}
                                >
                                    Is Continuous Workflow
                                </Checkbox>
                            </FormField>}
                    </ColumnLayout>
                </ColumnLayout>
            </Box>
        </>
    )
}

interface ScheduleWorkflowMetadataProps {
    isScheduled: boolean;
    scheduleName: string;
    onScheduleNameChange: (scheduleName: string) => void;
    cronExpression: string;
    onCronExpressionChange: (cronExpression: string) => void;
}

export const ScheduleWorkflowMetadata: React.FC<ScheduleWorkflowMetadataProps> = (props) => {
    const {scheduleName, cronExpression, onScheduleNameChange, onCronExpressionChange, isScheduled} = props
    return (
        <>
            {isScheduled &&
                <Box padding={{bottom: "l"}}>
                    <ColumnLayout columns={2} borders={"vertical"}>
                        <FormField
                            label="Schedule Name"
                        >
                            <Input value={scheduleName} onChange={(e) => onScheduleNameChange(e.detail.value)} data-testid="schedule-name-input"/>
                        </FormField>

                        <FormField
                            label={"Cron Expression"}
                            secondaryControl={<><Link
                                href={"https://docs.aws.amazon.com/eventbridge/latest/userguide/eb-create-rule-schedule.html#eb-cron-expressions"}
                                external={true}>
                                Refer this link</Link> for cron expression syntax</>}
                        >
                            <Input value={cronExpression} onChange={(e) => onCronExpressionChange(e.detail.value)}
                                   placeholder={"0 12 * * ? *"} data-testid="cron-expression-input"/>
                        </FormField>
                    </ColumnLayout>
                </Box>
            }
        </>
    )
}